/**
 * A light spreadsheet over any list of rows.
 *
 * Owns everything that is about the grid rather than what is in it: the
 * header that stays put while the rows scroll, one active cell moved by click
 * and the arrow keys, Enter / Space / F2 / typing to act on it, copy and paste,
 * Backspace to clear, and the marks those leave (a moving dashed border on the
 * copied cell, a red flash where a paste or clear is refused).
 *
 * The caller owns the columns and every write. A column says what Cmd+C and
 * Cmd+V mean for it through `meta`; anything that needs the caller's state
 * (clearing a cell, copying a whole record) comes back through a callback.
 *
 * This file is the wiring; each concern lives in its own hook or component.
 */

import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import {
  type SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { focusGrid, gridRootProps } from './dom';
import { HeaderCell } from './HeaderCell';
import { BlankRow, StandInRow } from './NewRow';
import { computePins, pinnedWidths } from './pinning';
import { SpreadsheetRow } from './SpreadsheetRow';
import type { SpreadsheetHandle, SpreadsheetProps } from './types';
import { NEW_ROW_ID, useCellMarks } from './useCellMarks';
import { useCellKeys } from './useCellKeys';
import { useCursor } from './useCursor';
import { belowDataRows, newRowActions, useStandIn } from './useNewRow';
import { usePinShadows } from './usePinShadows';
import { orderRows, useRowOrder } from './useRowOrder';

// Defaults kept outside the component, so they are the same every render and
// the memos built from them hold.
const NO_SORTING: SortingState = [];
const NO_HIDDEN_COLUMNS: readonly string[] = [];
const EVERY_ROW_EDITABLE = () => true;

function SpreadsheetInner<T>(
  {
    data,
    columns,
    getRowId,
    initialSorting = NO_SORTING,
    serverSort,
    hiddenColumnIds = NO_HIDDEN_COLUMNS,
    pinLeft = 0,
    pinRight = 0,
    columnFilters,
    rowClassName,
    emptyText = 'Nothing to show.',
    busy = false,
    maxHeight,
    canEditRow = EVERY_ROW_EDITABLE,
    onClearCell,
    onCopyStructured,
    onPasteStructured,
    newRow,
  }: SpreadsheetProps<T>,
  ref: React.ForwardedRef<SpreadsheetHandle>
) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeCellRef = useRef<HTMLTableCellElement | null>(null);

  // ── Rows and columns ────────────────────────────────────────────────────
  const order = useRowOrder(initialSorting);
  // A new object every render would defeat the table's own memos.
  const columnVisibility = useMemo(
    () => Object.fromEntries(hiddenColumnIds.map(id => [id, false])),
    [hiddenColumnIds]
  );
  const table = useReactTable({
    data,
    columns,
    state: { sorting: serverSort?.sorting ?? order.sorting, columnVisibility },
    onSortingChange: order.onSortingChange,
    manualSorting: !!serverSort,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => getRowId(row),
  });
  const sortedRows = table.getRowModel().rows;
  const rows = useMemo(
    () => orderRows(sortedRows, order.appendedIds),
    [sortedRows, order.appendedIds]
  );
  const rowIds = useMemo(() => rows.map(row => row.id), [rows]);
  const leafColumns = table.getVisibleLeafColumns();
  const columnIds = useMemo(
    () => leafColumns.map(column => column.id),
    [leafColumns]
  );
  const widths = useMemo(
    () => leafColumns.map(column => column.getSize()),
    [leafColumns]
  );
  const pins = useMemo(
    () => computePins(widths, pinLeft, pinRight),
    [widths, pinLeft, pinRight]
  );
  const pinned = pinnedWidths(pins, widths);

  // ── Below the data: the stand-in for a row being added, the blank row ───
  const standIn = useStandIn(rows, data);
  const { nextStandInIndex, standInIndex, newRowIndex, rowCount } =
    belowDataRows(rows.length, standIn.shown !== null, !!newRow);

  // ── Cursor, marks, keys ─────────────────────────────────────────────────
  const cursor = useCursor({
    scrollRef,
    activeCellRef,
    rowIds,
    rowCount,
    columnIds,
    pinnedLeft: pinned.left,
    pinnedRight: pinned.right,
    awaitedRowId: standIn.awaitedRowId,
  });
  const marks = useCellMarks();
  const { createRow, addRow } = newRowActions({
    newRow,
    nextStandInIndex,
    columnIds,
    standIn,
    append: order.append,
    cursor,
    refuse: marks.refuse,
  });
  const onKeyDown = useCellKeys({
    rootRef: scrollRef,
    activeCellRef,
    cursor: cursor.cursor,
    moveTo: cursor.moveTo,
    rows,
    rowCount,
    leafColumns,
    standInIndex,
    newRowIndex,
    newRow,
    createRow,
    getRowId,
    canEditRow,
    onClearCell,
    onCopyStructured,
    onPasteStructured,
    marks,
  });
  const updatePinShadows = usePinShadows(scrollRef);

  useImperativeHandle(ref, () => ({
    focus: () => focusGrid(scrollRef.current),
    focusCell: (rowId, columnId, edit = false) =>
      cursor.focusCell(rowId, columnId, edit),
    addRow,
  }));

  // ── Layout ──────────────────────────────────────────────────────────────
  const { refused, copied } = marks;
  const activeCol = (rowIndex: number) =>
    cursor.cursor.row === rowIndex ? cursor.cursor.col : -1;

  return (
    // One table: the header row sticks to the top as the rows scroll, so the
    // two cannot drift apart.
    <div className='overflow-hidden rounded-md border'>
      <div
        ref={scrollRef}
        {...gridRootProps}
        tabIndex={0}
        onKeyDown={onKeyDown}
        // No elastic overscroll: it would drag the pinned columns and the
        // header along with the rows. The right padding keeps the last column
        // clear of the scroll bar, which on an overlay-scrollbar system (macOS)
        // draws over the content and takes no gutter.
        className='overflow-auto overscroll-none pb-3 pr-3 outline-none [scrollbar-gutter:stable] [scrollbar-width:thin]'
        style={{ maxHeight }}
        onScroll={updatePinShadows}
      >
        <table
          aria-busy={busy}
          className={`w-full table-fixed border-separate border-spacing-0 text-[13px] ${busy ? 'opacity-60' : ''}`}
          style={{ minWidth: table.getTotalSize() }}
        >
          <colgroup>
            {leafColumns.map(column => (
              <col key={column.id} style={{ width: column.getSize() }} />
            ))}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <HeaderCell
                    key={header.id}
                    header={header}
                    pin={pins[header.index]}
                    filter={columnFilters?.[header.column.id]}
                    onSort={serverSort?.onSort}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <SpreadsheetRow
                key={row.id}
                row={row}
                rowIndex={rowIndex}
                className={rowClassName?.(row.original)}
                columns={columns}
                pins={pins}
                activeCol={activeCol(rowIndex)}
                activeCellRef={activeCellRef}
                refusedColumnId={
                  refused?.rowId === row.id ? refused.columnId : null
                }
                refusedAt={refused?.rowId === row.id ? refused.at : 0}
                copiedColumnId={
                  copied?.rowId === row.id ? copied.columnId : null
                }
                onSelectCell={cursor.selectCell}
              />
            ))}
            {standIn.shown && (
              <StandInRow
                standIn={standIn.shown}
                leafColumns={leafColumns}
                pins={pins}
                activeCol={activeCol(standInIndex)}
                activeCellRef={activeCellRef}
              />
            )}
            {newRow && (
              <BlankRow
                columns={newRow.columns}
                rowIndex={newRowIndex}
                creating={standIn.creating}
                refusedColumnId={
                  refused?.rowId === NEW_ROW_ID ? refused.columnId : null
                }
                refusedAt={refused?.rowId === NEW_ROW_ID ? refused.at : 0}
                onSelectCell={cursor.selectCell}
                onCreate={createRow}
                leafColumns={leafColumns}
                pins={pins}
                activeCol={activeCol(newRowIndex)}
                activeCellRef={activeCellRef}
              />
            )}
            {rows.length === 0 && !newRow && (
              <tr>
                <td
                  colSpan={leafColumns.length}
                  className='py-8 text-center text-muted-foreground'
                >
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const Spreadsheet = forwardRef(SpreadsheetInner) as <T>(
  props: SpreadsheetProps<T> & { ref?: React.Ref<SpreadsheetHandle> }
) => ReturnType<typeof SpreadsheetInner>;
